/**
 * One-question-at-a-time async confirm gate — the promise half of a confirm modal.
 *
 * `ask(request)` returns a promise that settles when someone calls `settle(answer)`;
 * `onChange(request | null)` fires as the open question appears and clears, which is what
 * a React hook mirrors into state to show/hide the modal. Only an explicit `true` counts as
 * a yes. Asking again while a question is open DECLINES the earlier asker — two open spend
 * confirmations at once would be two chances to click the wrong one. Pure (no React), so
 * the gating rule is testable without a render.
 */
export function createConfirmGate(onChange) {
  let open = null; // { request, resolve } | null
  const emit = () => onChange?.(open ? { ...open.request } : null);
  return {
    ask(request) {
      return new Promise((resolve) => {
        const superseded = open;
        open = { request, resolve };
        superseded?.resolve(false);
        emit();
      });
    },
    settle(answer) {
      if (!open) return false;
      const { resolve } = open;
      open = null;
      emit();
      resolve(answer === true);
      return true;
    },
    pending() {
      return open ? { ...open.request } : null;
    },
  };
}
